import React from 'react'

import clsx from 'clsx'

import Grid from 'components/Grid'
import LazyContent from 'components/LazyContent'
import { ReviewCard } from 'components/Review'
import SoftwareApplicationLD from 'components/SoftwareApplicationLD'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { getReviewProps } from 'helpers/comparisonReviewsHelpers'
import getReviewPermalink from 'helpers/getReviewPermalink'
import openUrl from 'helpers/openUrl'
import openWindow from 'helpers/openWindow'

import classes from './ReviewsSection.module.scss'

/** @type {import("./types").default} */
const ReviewsSection = ({ title = '', reviews = [], mobile = false, listings = [], lazy = false }) => {
  const { sendEvent } = useEventContext()

  const handleShareButtonClick = (_, { name, url }) => {
    sendEvent({
      engagementComponent: 'product_reviews_comparison_card-item',
      engagementDestination: name === 'link' ? 'go-to_product_reviews_single-review' : `go-to_social_${name}`,
      engagementDetails: name === 'link' ? 'icon_link' : `icon_social_${name}`,
      name: name === 'link' ? 'engagement_product_click' : 'click',
      ...(name === 'link' ? {} : { outboundLinkType: 'social link', outboundSocialLinkClickCount: 1 }),
    })

    if (name !== 'link') {
      openWindow(url)
    } else {
      openUrl(url)
    }
  }

  const Wrapper = lazy ? LazyContent : React.Fragment
  const wrapperProps = lazy ? { placeholderClassname: classes.lazyLoading } : undefined

  return (
    <Grid
      container
      className={clsx('ReviewsSection', classes.comparisonReviewsWrapper)}
      data-testid="comparison-reviews"
    >
      <Grid item md={12}>
        <Typography variant={mobile ? 'heading-md' : 'heading-lg'} className={classes.comparisonReviewsIntro}>
          {title}
        </Typography>
        {reviews.first && (
          <>
            <SoftwareApplicationLD listing={listings[0]} />
            {reviews.first.map(review => (
              <div className={classes.reviewCardWrapper} key={`comparisons-review-first-${review.id}`}>
                <Wrapper {...wrapperProps}>
                  <ReviewCard
                    review={review}
                    {...getReviewProps(review, listings[0])}
                    listingDetailUrl={listings[0].cta_urls.detail}
                    listingName={listings[0].name}
                    listingLogoUrl={listings[0].logo_url}
                    permalink={getReviewPermalink(listings[0], review)}
                    onExpandClick={(_, { isExpanded, name }) => {
                      sendEvent({
                        engagementComponent: 'product_reviews_comparison_card-item',
                        engagementDestination: isExpanded ? 'expand-text' : 'collapse-text',
                        engagementDetails: `text-link_read-${isExpanded ? 'more' : 'less'}-${name}`,
                        name: 'engagement_product_click',
                      })
                    }}
                    onExpandPanelClick={(_, { isExpanded, name }) => {
                      sendEvent({
                        engagementComponent: 'product_reviews_comparison_card-item',
                        engagementDestination: isExpanded ? 'expand-card' : 'collapse-card',
                        engagementDetails: `expansion-panel_${name}`,
                        name: 'engagement_product_click',
                      })
                    }}
                    onListingIconNameClick={event => {
                      event.stopPropagation()

                      sendEvent({
                        engagementComponent: 'ua_click_details',
                        engagementDestination: 'ua_app_name_reviews_card',
                        engagementDetails: 'ua_compare',
                        name: 'engagement_product_click',
                      })
                    }}
                    onShareButtonClick={handleShareButtonClick}
                    withListingIconName
                  />
                </Wrapper>
              </div>
            ))}
          </>
        )}
        {reviews.last && (
          <>
            <SoftwareApplicationLD listing={listings[1]} />

            {reviews.last.map(review => (
              <div className={classes.reviewCardWrapper} key={`comparisons-review-last-${review.id}`}>
                <Wrapper {...wrapperProps}>
                  <ReviewCard
                    review={review}
                    {...getReviewProps(review, listings[1])}
                    listingDetailUrl={listings[1].cta_urls.detail}
                    listingName={listings[1].name}
                    listingLogoUrl={listings[1].logo_url}
                    permalink={getReviewPermalink(listings[1], review)}
                    onExpandClick={(_, { isExpanded, name }) => {
                      sendEvent({
                        engagementComponent: 'product_reviews_comparison_card-item',
                        engagementDestination: isExpanded ? 'expand-text' : 'collapse-text',
                        engagementDetails: `text-link_read-${isExpanded ? 'more' : 'less'}-${name}`,
                        name: 'engagement_product_click',
                      })
                    }}
                    onExpandPanelClick={(_, { isExpanded, name }) => {
                      sendEvent({
                        engagementComponent: 'product_reviews_comparison_card-item',
                        engagementDestination: isExpanded ? 'expand-card' : 'collapse-card',
                        engagementDetails: `expansion-panel_${name}`,
                        name: 'engagement_product_click',
                      })
                    }}
                    onListingIconNameClick={event => {
                      event.stopPropagation()

                      sendEvent({
                        engagementComponent: 'ua_click_details',
                        engagementDestination: 'ua_app_name_reviews_card',
                        engagementDetails: 'ua_compare',
                        name: 'engagement_product_click',
                      })
                    }}
                    onShareButtonClick={handleShareButtonClick}
                    withListingIconName
                  />
                </Wrapper>
              </div>
            ))}
          </>
        )}
      </Grid>
    </Grid>
  )
}

export default ReviewsSection
